import React from 'react';
import { cn } from '@/shared/utils/cn';

type BadgeVariant =
  // Generic variants
  | 'default'
  | 'primary'
  | 'secondary'
  | 'success'
  | 'warning'
  | 'danger'
  | 'info'
  // Catalyst-compatible color variants (20 colors)
  | 'red'
  | 'orange'
  | 'amber'
  | 'yellow'
  | 'lime'
  | 'green'
  | 'emerald'
  | 'teal'
  | 'cyan'
  | 'sky'
  | 'blue'
  | 'indigo'
  | 'violet'
  | 'purple'
  | 'fuchsia'
  | 'pink'
  | 'rose'
  | 'zinc'
  | 'white'
  | 'dark'
  // CRM-specific variants
  | 'lead-hot'
  | 'lead-warm'
  | 'lead-cold'
  | 'client-healthy'
  | 'client-stable'
  | 'client-risk'
  | 'client-critical';

interface BadgeProps extends React.HTMLAttributes<HTMLSpanElement> {
  children: React.ReactNode;
  variant?: BadgeVariant;
  size?: 'xs' | 'sm' | 'md' | 'lg';
  dot?: boolean;
  removable?: boolean;
  onRemove?: () => void;
}

export const Badge = React.forwardRef<HTMLSpanElement, BadgeProps>(
  ({
    children,
    variant = 'default',
    size = 'sm',
    dot = false,
    removable = false,
    onRemove,
    className,
    ...props
  }, ref) => {
    const baseClasses = 'inline-flex items-center font-medium transition-all duration-200 rounded-full shrink-0 overflow-hidden';

    const variantClasses: Record<BadgeVariant, string> = {
      // Generic variants
      default: 'bg-gray-100 text-gray-800 dark:bg-slate-700 dark:text-slate-200',
      primary: 'bg-primary-100 text-primary-800 dark:bg-primary-900/30 dark:text-primary-300',
      secondary: 'bg-gray-100 text-gray-700 dark:bg-slate-700 dark:text-slate-300',
      success: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
      warning: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
      danger: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
      info: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',

      // Catalyst-compatible colors (20 colors) - Optimized for light & dark mode
      red: 'bg-red-500/15 text-red-700 dark:bg-red-500/10 dark:text-red-400',
      orange: 'bg-orange-500/15 text-orange-700 dark:bg-orange-500/10 dark:text-orange-400',
      amber: 'bg-amber-400/20 text-amber-700 dark:bg-amber-400/10 dark:text-amber-400',
      yellow: 'bg-yellow-400/20 text-yellow-700 dark:bg-yellow-400/10 dark:text-yellow-400',
      lime: 'bg-lime-400/20 text-lime-700 dark:bg-lime-400/10 dark:text-lime-400',
      green: 'bg-green-500/15 text-green-700 dark:bg-green-500/10 dark:text-green-400',
      emerald: 'bg-emerald-500/15 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-400',
      teal: 'bg-teal-500/15 text-teal-700 dark:bg-teal-500/10 dark:text-teal-400',
      cyan: 'bg-cyan-500/15 text-cyan-700 dark:bg-cyan-500/10 dark:text-cyan-400',
      sky: 'bg-sky-500/15 text-sky-700 dark:bg-sky-500/10 dark:text-sky-400',
      blue: 'bg-blue-500/15 text-blue-700 dark:bg-blue-500/10 dark:text-blue-400',
      indigo: 'bg-indigo-500/15 text-indigo-700 dark:bg-indigo-500/10 dark:text-indigo-400',
      violet: 'bg-violet-500/15 text-violet-700 dark:bg-violet-500/10 dark:text-violet-400',
      purple: 'bg-purple-500/15 text-purple-700 dark:bg-purple-500/10 dark:text-purple-400',
      fuchsia: 'bg-fuchsia-500/15 text-fuchsia-700 dark:bg-fuchsia-500/10 dark:text-fuchsia-400',
      pink: 'bg-pink-500/15 text-pink-700 dark:bg-pink-500/10 dark:text-pink-400',
      rose: 'bg-rose-500/15 text-rose-700 dark:bg-rose-500/10 dark:text-rose-400',
      zinc: 'bg-zinc-500/15 text-zinc-700 dark:bg-zinc-500/10 dark:text-zinc-400',
      white: 'bg-white text-gray-900 dark:bg-white/10 dark:text-white ring-1 ring-inset ring-gray-200 dark:ring-white/20',
      dark: 'bg-gray-900/90 text-white dark:bg-white/10 dark:text-white dark:ring-1 dark:ring-inset dark:ring-white/20',

      // CRM Specific - Lead Status
      'lead-hot': 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
      'lead-warm': 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
      'lead-cold': 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',

      // CRM Specific - Client Health
      'client-healthy': 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
      'client-stable': 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
      'client-risk': 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
      'client-critical': 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
    };

    const sizeClasses = {
      xs: 'px-1.5 py-0.5 text-xs gap-1',
      sm: 'px-2 py-1 text-xs gap-1',
      md: 'px-2.5 py-1 text-sm gap-1.5',
      lg: 'px-3 py-1.5 text-sm gap-2'
    };

    const dotClasses = {
      xs: 'w-1.5 h-1.5',
      sm: 'w-2 h-2',
      md: 'w-2 h-2',
      lg: 'w-2.5 h-2.5'
    };

    const handleRemove = (e: React.MouseEvent) => {
      e.stopPropagation();
      onRemove?.();
    };

    // Renderizar children preservando iconos en la misma línea
    const renderChildren = () => {
      if (!children) return null;

      // Si children es un array o fragmento (ej: [<Icon />, "Texto"])
      if (Array.isArray(children) || (React.isValidElement(children) && children.type === React.Fragment)) {
        return <span className="inline-flex items-center gap-1 min-w-0 overflow-hidden">{children}</span>;
      }

      // Si es un solo elemento, return directo
      return children;
    };

    return (
      <span
        ref={ref}
        className={cn(
          baseClasses,
          variantClasses[variant],
          sizeClasses[size],
          className
        )}
        {...props}
      >
        {dot && (
          <span
            className={cn(
              'rounded-full bg-current opacity-75 shrink-0 flex-shrink-0',
              dotClasses[size]
            )}
            aria-hidden="true"
          />
        )}
        {renderChildren()}
        {removable && (
          <button
            type="button"
            onClick={handleRemove}
            className="ml-0.5 shrink-0 rounded-full p-0.5"
            aria-label="Remover badge"
          >
            <svg
              className={cn(
                'text-current',
                size === 'xs' ? 'w-3 h-3' : 'w-4 h-4'
              )}
              fill="none"
              strokeWidth="2"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </span>
    );
  }
);

Badge.displayName = 'Badge';

// Helper functions for CRM-specific badges
export const LeadBadge: React.FC<{
  temperature: 'hot' | 'warm' | 'cold';
  children?: React.ReactNode;
  className?: string;
}> = ({ temperature, children, className }) => {
  const labels = {
    hot: 'Caliente',
    warm: 'Tibio',
    cold: 'Frío'
  };

  return (
    <Badge
      variant={`lead-${temperature}` as any}
      dot
      className={className}
    >
      {children || labels[temperature]}
    </Badge>
  );
};

export const ClientHealthBadge: React.FC<{
  health: 'healthy' | 'stable' | 'risk' | 'critical';
  children?: React.ReactNode;
  className?: string;
}> = ({ health, children, className }) => {
  const labels = {
    healthy: 'Saludable',
    stable: 'Estable',
    risk: 'En Riesgo',
    critical: 'Crítico'
  };

  return (
    <Badge
      variant={`client-${health}` as any}
      dot
      className={className}
    >
      {children || labels[health]}
    </Badge>
  );
};